import { inject, injectable } from 'inversify'
import { nanoid } from 'nanoid'
import { Group, Vector3 } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import type { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { setup } from 'xstate'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { DrawUtils, type ILine, type IPoint } from '@/plugins/cad/module/utils'
import { KeyCodes } from '@/shared/constants'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { DrawEntityCommand } from '../command/DrawEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import {
  AngularDimensionEntity,
  getAngularDimensionData,
  getAngularDimensionLegLines,
} from '../model/entity'
import {
  createCadLine2Object,
  createCadLineSegments2Object,
} from '../object/material/CadLineFactory'
import type { LineTypeMaterial } from '../object/material/MaterialManager'
import { MaterialUtils } from '../object/material/MaterialUtils'
import type { EntityDetector } from '../selector/entityDetector'
import type { EntitySelector } from '../selector/entitySelector'
import {
  type AngularDimensionSegmentCandidate,
  buildAngularDimensionSegmentCandidates,
  getAngularDimensionSegmentDistance,
  resolveAngularDimensionLinePair,
} from './angularDimensionSegmentUtils'

const AngularDimensionMouseTip = {
  firstLine: CadTipKey.draw.angularDimension.firstLine,
  secondLine: CadTipKey.draw.angularDimension.secondLine,
  position: CadTipKey.draw.angularDimension.position,
  parallelLinesRetry: CadTipKey.draw.angularDimension.parallelLinesRetry,
}

const angularDimensionMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
    entrySpecifyPosition: () => {},
    entryFinished: () => {},
  },
}).createMachine({
  id: 'drawAngularDimensionTool',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: 'draw',
      },
    },
    draw: {
      initial: 'line1',
      states: {
        line1: {
          on: { NEXT: 'line2' },
          entry: { type: 'showMouseTip', params: AngularDimensionMouseTip.firstLine },
        },
        line2: {
          on: { NEXT: 'position', PREVIOUS: 'line1' },
          entry: { type: 'showMouseTip', params: AngularDimensionMouseTip.secondLine },
        },
        position: {
          on: { NEXT: '#drawAngularDimensionTool.finished', PREVIOUS: 'line2' },
          entry: [
            { type: 'showMouseTip', params: AngularDimensionMouseTip.position },
            'entrySpecifyPosition',
          ],
        },
      },
    },
    finished: { entry: 'entryFinished', type: 'final' },
  },
})

/**
 * 角度标注绘制工具：依次选择两条参考线并指定标注弧的位置。
 */
@injectable()
export class DrawAngularDimensionTool extends StateMachineTool<typeof angularDimensionMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawAngularDimensionTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.EntityDetector)
  private readonly entityDetector!: EntityDetector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  protected machine = angularDimensionMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
      entrySpecifyPosition: () => {
        this.ensureInputBox()
      },
      entryFinished: () => {
        this.updateEntity()
        this.exit()
      },
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController

  private selectedLineCandidates: AngularDimensionSegmentCandidate[] = []
  private currentLineCandidate: AngularDimensionSegmentCandidate | null = null
  private tempPoint: IPoint | null = null
  private group = new Group()
  private selectedLineObject: LineSegments2 | null = null
  private hoverLineObject: LineSegments2 | null = null
  private previewLineObject: LineSegments2 | null = null
  private previewArcObject: Line2 | null = null
  private selectedLineMaterial: LineTypeMaterial | null = null
  private hoverLineMaterial: LineTypeMaterial | null = null
  private previewMaterial: LineTypeMaterial | null = null
  private entity: AngularDimensionEntity | null = null
  private currentCommand: DrawEntityCommand | null = null
  private inputElement: HTMLInputElement | null = null

  protected onInitialize(entityData?: Partial<AngularDimensionEntity>): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawEntityCommand(
      this.entity,
      this.viewManager,
      this.handleUndo.bind(this),
      this.isCanCommit.bind(this),
      this.isCanUndoStep.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    if (this.selectedLineObject) {
      this.selectedLineObject.geometry.dispose()
      this.selectedLineObject.parent?.remove(this.selectedLineObject)
    }
    if (this.hoverLineObject) {
      this.hoverLineObject.geometry.dispose()
      this.hoverLineObject.parent?.remove(this.hoverLineObject)
    }
    if (this.previewLineObject) {
      this.previewLineObject.geometry.dispose()
      this.previewLineObject.parent?.remove(this.previewLineObject)
    }
    if (this.previewArcObject) {
      this.previewArcObject.geometry.dispose()
      this.previewArcObject.parent?.remove(this.previewArcObject)
    }
    this.previewMaterial?.dispose()
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.removeInputBox()

    this.selectedLineCandidates = []
    this.currentLineCandidate = null
    this.tempPoint = null
    this.selectedLineObject = null
    this.hoverLineObject = null
    this.previewLineObject = null
    this.previewArcObject = null
    this.selectedLineMaterial = null
    this.hoverLineMaterial = null
    this.previewMaterial = null
    this.entity = null
    this.currentCommand = null
  }

  /**
   * 鼠标按下：依次选中两段候选线，随后在位置阶段确认弧所在点。
   *
   * @param params - 鼠标事件参数
   */
  onMouseDown(params: MouseEventParams) {
    if (params.event.button !== 0) {
      return
    }

    if (this.selectedLineCandidates.length < 2) {
      if (!this.currentLineCandidate) {
        return
      }
      if (
        this.selectedLineCandidates.some(
          (candidate) => candidate.id === this.currentLineCandidate!.id
        )
      ) {
        return
      }
      if (this.selectedLineCandidates.length > 0) {
        if (!this.canCreateDimension(this.selectedLineCandidates[0], this.currentLineCandidate)) {
          this.showMouseTip(AngularDimensionMouseTip.parallelLinesRetry)
          return
        }
      }

      this.selectedLineCandidates.push(this.currentLineCandidate)
      this.currentLineCandidate = null
      this.send({ type: 'NEXT' })
      this.updateCanvas()
      this.historyManager.refresh()
      return
    }

    if (this.tempPoint && this.getDimensionData()) {
      this.send({ type: 'NEXT' })
    }
  }

  /**
   * 鼠标移动：候选阶段更新悬停参考线，位置阶段刷新预览弧。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (this.selectedLineCandidates.length < 2) {
      this.currentLineCandidate = this.getCurrentLineCandidate(params)
      this.tempPoint = null
      this.updateCanvas()
      return
    }

    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  /** 每帧刷新标注数值输入框的位置与可见性 */
  onViewerUpdate() {
    this.updateInputBox()
  }

  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  private ensureInputBox() {
    if (this.inputElement) {
      return
    }

    this.inputElement = this.createInputBox()
  }

  private createInputBox() {
    const inputElement = document.createElement('input')
    inputElement.readOnly = true
    inputElement.tabIndex = -1
    Object.assign(inputElement.style, {
      position: 'absolute',
      width: '72px',
      maxWidth: '-webkit-fill-available',
      outline: 'none',
      backgroundColor: '#fff',
      color: '#000',
      visibility: 'hidden',
      transform: 'translate(-50%, -100%)',
      pointerEvents: 'none',
    })
    this.controller.domElement?.parentElement?.appendChild(inputElement)
    return inputElement
  }

  private removeInputBox() {
    if (!this.inputElement) {
      return
    }
    this.inputElement.parentElement?.removeChild(this.inputElement)
    this.inputElement = null
  }

  private updateInputBox() {
    if (!this.inputElement) {
      return
    }

    const dimensionData = this.getDimensionData()
    if (!dimensionData) {
      this.inputElement.style.visibility = 'hidden'
      return
    }

    const screenPoint = this.controller.getScreenPosition(this.getArcMiddlePoint(dimensionData))
    Object.assign(this.inputElement.style, {
      left: `${screenPoint.x}px`,
      top: `${screenPoint.y}px`,
    })
    this.inputElement.style.visibility = 'visible'
    this.inputElement.value = dimensionData.angleDegrees.toFixed(2)
  }

  private updateCanvas() {
    this.updateSelectedLineObject()
    this.updateHoverLineObject()
    const dimensionData = this.getDimensionData()
    this.updatePreviewLine(dimensionData)
    this.updatePreviewArc(dimensionData)
    this.updateInputBox()
  }

  private updateSelectedLineObject() {
    const lines = this.selectedLineCandidates.flatMap((candidate) => candidate.previewLines)
    if (lines.length === 0) {
      this.selectedLineObject && (this.selectedLineObject.visible = false)
      return
    }

    const geometry = DrawUtils.getLineSegmentsGeometryByLines(lines)
    if (!this.selectedLineObject) {
      this.selectedLineObject = createCadLineSegments2Object(
        geometry,
        this.getSelectedLineMaterial()!
      )
      this.selectedLineObject.computeLineDistances()
      this.group.add(this.selectedLineObject)
      return
    }

    this.selectedLineObject.visible = true
    this.selectedLineObject.geometry.dispose()
    this.selectedLineObject.geometry = geometry
    this.selectedLineObject.computeLineDistances()
  }

  private updateHoverLineObject() {
    const candidate =
      this.currentLineCandidate &&
      !this.selectedLineCandidates.some((item) => item.id === this.currentLineCandidate!.id)
        ? this.currentLineCandidate
        : null
    if (!candidate) {
      this.hoverLineObject && (this.hoverLineObject.visible = false)
      return
    }

    const geometry = DrawUtils.getLineSegmentsGeometryByLines(candidate.previewLines)
    if (!this.hoverLineObject) {
      this.hoverLineObject = createCadLineSegments2Object(geometry, this.getHoverLineMaterial()!)
      this.hoverLineObject.computeLineDistances()
      this.group.add(this.hoverLineObject)
      return
    }

    this.hoverLineObject.visible = true
    this.hoverLineObject.geometry.dispose()
    this.hoverLineObject.geometry = geometry
    this.hoverLineObject.computeLineDistances()
  }

  private updatePreviewLine(
    dimensionData: ReturnType<DrawAngularDimensionTool['getDimensionData']>
  ) {
    if (!this.entity || !dimensionData) {
      this.previewLineObject && (this.previewLineObject.visible = false)
      return
    }

    const { line1, line2 } = resolveAngularDimensionLinePair(
      this.selectedLineCandidates[0],
      this.selectedLineCandidates[1]
    )
    const lines: ILine[] = getAngularDimensionLegLines({ line1, line2 }, dimensionData)

    const geometry = DrawUtils.getLineSegmentsGeometryByLines(lines)
    if (!this.previewLineObject) {
      this.previewLineObject = createCadLineSegments2Object(geometry, this.getPreviewMaterial()!)
      this.previewLineObject.computeLineDistances()
      this.group.add(this.previewLineObject)
      return
    }

    this.previewLineObject.visible = true
    this.previewLineObject.geometry.dispose()
    this.previewLineObject.geometry = geometry
    this.previewLineObject.computeLineDistances()
  }

  private updatePreviewArc(
    dimensionData: ReturnType<DrawAngularDimensionTool['getDimensionData']>
  ) {
    if (!dimensionData) {
      this.previewArcObject && (this.previewArcObject.visible = false)
      return
    }

    const geometry = DrawUtils.getLineGeometryByPoints(
      DrawUtils.getArcPoints({
        center: { x: 0, y: 0 },
        radius: dimensionData.radius,
        startAngle: dimensionData.startAngle,
        endAngle: dimensionData.endAngle,
        clockwise: dimensionData.clockwise,
      })
    )

    if (!this.previewArcObject) {
      this.previewArcObject = createCadLine2Object(geometry, this.getPreviewMaterial()!)
      this.previewArcObject.position.copy(dimensionData.intersection)
      this.previewArcObject.computeLineDistances()
      this.group.add(this.previewArcObject)
      return
    }

    this.previewArcObject.visible = true
    this.previewArcObject.geometry.dispose()
    this.previewArcObject.geometry = geometry
    this.previewArcObject.position.copy(dimensionData.intersection)
    this.previewArcObject.computeLineDistances()
  }

  private getPreviewMaterial() {
    if (!this.previewMaterial) {
      if (!this.entity) {
        return undefined
      }
      this.previewMaterial = MaterialUtils.createMaterialByLineType(
        this.entity.lineType,
        MaterialUtils.getMaterialOptionByEntity(this.entity)
      )
    }
    return this.previewMaterial
  }

  private getSelectedLineMaterial() {
    if (!this.selectedLineMaterial) {
      if (!this.entity) {
        return undefined
      }
      this.selectedLineMaterial = this.viewManager.objectManager
        .getMaterialManager()
        .getCachedSelectMaterial(this.entity)
    }
    return this.selectedLineMaterial
  }

  private getHoverLineMaterial() {
    if (!this.hoverLineMaterial) {
      if (!this.entity) {
        return undefined
      }
      this.hoverLineMaterial = this.viewManager.objectManager
        .getMaterialManager()
        .getCachedHoverMaterial(this.entity)
    }
    return this.hoverLineMaterial
  }

  private createEntity(entityData?: Partial<AngularDimensionEntity>) {
    const entity = new AngularDimensionEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
    })
    if (entityData?.textHeight) {
      entity.textHeight = entityData.textHeight
    }
    return entity
  }

  private updateEntity() {
    if (!this.entity || this.selectedLineCandidates.length < 2 || !this.tempPoint) {
      return
    }
    const { line1, line2 } = resolveAngularDimensionLinePair(
      this.selectedLineCandidates[0],
      this.selectedLineCandidates[1]
    )
    const dimensionData = getAngularDimensionData(line1, line2, this.tempPoint, this.tempPoint)
    if (!dimensionData) {
      return
    }
    const textPosition = this.getArcMiddlePoint(dimensionData)

    Object.assign(this.entity, {
      line1,
      line2,
      arcPoint: { x: this.tempPoint.x, y: this.tempPoint.y },
      textPosition: { x: textPosition.x, y: textPosition.y },
      dimensionText: '',
    })
  }

  private handleUndo() {
    if (!this.isCanUndoStep()) {
      return
    }

    this.selectedLineCandidates.pop()
    this.currentLineCandidate = null
    this.tempPoint = null
    this.removeInputBox()
    this.updateCanvas()
    this.send({ type: 'PREVIOUS' })
  }

  private isCanCommit() {
    return !!this.entity?.arcPoint
  }

  private isCanUndoStep() {
    return this.selectedLineCandidates.length > 0
  }

  private canCreateDimension(
    first: AngularDimensionSegmentCandidate,
    second: AngularDimensionSegmentCandidate
  ) {
    const { line1, line2 } = resolveAngularDimensionLinePair(first, second)
    const previewPoint = this.getLineMidPoint(line2)
    return !!getAngularDimensionData(line1, line2, previewPoint, previewPoint)
  }

  private getCurrentLineCandidate(params: MouseEventParams) {
    const entities = this.entityDetector.handleDetect(params.screenPos)
    const threshold = this.controller.getPixelSizeInWorld(12)
    let candidate: AngularDimensionSegmentCandidate | null = null
    let minDistance = threshold

    for (const entity of entities) {
      const candidates = buildAngularDimensionSegmentCandidates(entity)
      for (const item of candidates) {
        const distance = getAngularDimensionSegmentDistance(params.worldPos, item)
        if (distance <= minDistance) {
          minDistance = distance
          candidate = item
        }
      }
    }

    return candidate
  }

  private getDimensionData() {
    if (this.selectedLineCandidates.length < 2 || !this.tempPoint) {
      return null
    }
    const { line1, line2 } = resolveAngularDimensionLinePair(
      this.selectedLineCandidates[0],
      this.selectedLineCandidates[1]
    )
    return getAngularDimensionData(line1, line2, this.tempPoint, this.tempPoint)
  }

  private getArcMiddlePoint(
    dimensionData: NonNullable<ReturnType<DrawAngularDimensionTool['getDimensionData']>>
  ) {
    const middleAngle = dimensionData.startAngle + dimensionData.sweepAngle / 2
    return new Vector3(
      dimensionData.intersection.x + Math.cos(middleAngle) * dimensionData.radius,
      dimensionData.intersection.y + Math.sin(middleAngle) * dimensionData.radius,
      dimensionData.intersection.z
    )
  }

  private getLineMidPoint(line: ILine) {
    return {
      x: (line.startPoint.x + line.endPoint.x) / 2,
      y: (line.startPoint.y + line.endPoint.y) / 2,
    }
  }
}
